import { Form, FormControl, FormField, FormItem, FormMessage } from '@cibli/shared';
import { BoothOtpMode } from '@cibli/shared';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '@/features/common/components/IconButton.tsx';
import MainLayout from '@/features/common/components/MainLayout';
import PhoneInput from '@/features/common/components/PhoneInput.tsx';
import { useBooth } from '@/features/settings/hooks/useBooth';

import FormButtons from '../components/FormButtons';
import { UserInfo } from '../context/register.context';
import { useRegisterContext } from '../hooks/useRegisterContext';

interface Props {
  onCancel: () => void;
  onBack?: () => void;
  onNext: (userInfos: UserInfo) => void | Promise<void>;
  error?: string | null;
  isLoading?: boolean;
}

export default function PhoneScreen({ onCancel, onBack, onNext, error, isLoading }: Props) {
  const { t } = useTranslation();
  const { booth } = useBooth();
  const { phoneForm, namesForm, cguForm, cityForm } = useRegisterContext();
  const { firstName } = namesForm.watch();

  // In EMAIL mode the candidate receives the code by email: the phone stays optional
  const isPhoneOptional = booth?.otpMode === BoothOtpMode.EMAIL;

  // Local state drives the displayed input (immediate re-render on keystroke);
  // the form value is kept in sync for submit/validation
  const [phone, setPhone] = useState<string>(() => phoneForm.getValues('phone') || '');
  const [phoneComplete, setPhoneComplete] = useState(false);

  const handlePhoneChange = useCallback(
    (phone: string, isComplete: boolean) => {
      setPhone(phone);
      setPhoneComplete(isComplete);
      phoneForm.setValue('phone', phone, { shouldValidate: true });
    },
    [phoneForm],
  );

  const getUserInfos = useCallback((): UserInfo => {
    const namesData = namesForm.getValues();
    return {
      firstName: namesData.firstName,
      lastName: namesData.lastName,
      email: namesData.email ?? undefined,
      ...phoneForm.getValues(),
      ...cguForm.getValues(),
      ...cityForm.getValues(),
    };
  }, [namesForm, phoneForm, cguForm, cityForm]);

  const handleSubmit = useCallback(() => {
    // An empty phone is allowed when the booth OTP mode makes it optional
    if (!phoneComplete && !(isPhoneOptional && !phone)) return;
    void onNext(getUserInfos());
  }, [onNext, getUserInfos, phoneComplete, isPhoneOptional, phone]);

  if (isLoading) {
    return (
      <MainLayout className="gap-6 text-center" onClose={onCancel}>
        <motion.div
          className="flex flex-col items-center gap-6"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <motion.img
            src="/logo-ciblijob.svg"
            alt="CibliJob"
            className="h-16 w-auto"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="flex flex-col items-center gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            <p className="text-lg text-muted-foreground">
              {t('FormScreen.Phone.verifying') || 'Vérification en cours...'}
            </p>
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  className="h-2 w-2 rounded-full bg-primary"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      className="gap-4 sm:gap-6 text-center"
      onClose={onCancel}
      rightContent={
        onBack ? (
          <IconButton
            className="ml-6"
            icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" />}
            onClick={onBack}
          />
        ) : undefined
      }
    >
      <h3 className="text-lg sm:text-xl text-primary">
        {t('FormScreen.Phone.hint', { name: firstName })}
      </h3>
      <h1 className="text-3xl md:text-4xl lg:text-5xl text-balance">
        {t('FormScreen.Phone.title')}
      </h1>
      <p className="text-lg md:text-xl lg:text-2xl max-w-xl text-balance text-muted-foreground">
        {t('FormScreen.Phone.description')}
      </p>

      <Form {...phoneForm}>
        <form
          onSubmit={phoneForm.handleSubmit(handleSubmit)}
          className="w-full flex flex-col gap-4 max-w-md mx-auto"
        >
          <FormField
            control={phoneForm.control}
            name="phone"
            render={() => (
              <FormItem className="mt-4 text-left">
                <FormControl>
                  <PhoneInput
                    value={phone}
                    onChange={handlePhoneChange}
                    showDialHint
                    autoFocus
                  />
                </FormControl>
                <FormMessage />
                {isPhoneOptional && (
                  <p className="text-xs text-muted-foreground pt-1">
                    {t('FormScreen.Phone.optionalHint')}
                  </p>
                )}
                {error && <p className="text-sm font-medium text-destructive mt-1">{error}</p>}
              </FormItem>
            )}
          />

          <FormButtons
            disableNextButton={!phone ? !isPhoneOptional : !phoneComplete}
            nextLabel={isPhoneOptional && !phone ? t('FormScreen.Phone.skip') : undefined}
            showBackButton={!!onBack}
            onBack={onBack}
            stepName="phone"
          />
        </form>
      </Form>
    </MainLayout>
  );
}
